import { supabase } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import { Message, OrderUpdate } from '@/types/munshi';
import { LineItem } from '@/types/models/Invoice';
import { StoreUnit } from '@/constants/units';
import { InvoiceAgent, AgentContext } from '../agents/InvoiceAgent';
import { ChatSessionContext } from '@/munshi/types/context';

interface ProcessResult {
  message: string;
  actions: OrderUpdate['actions'];
}

interface BaseAction {
  type: string;
}

interface RemoveAction extends BaseAction {
  type: 'remove';
  itemId: string;
}

interface AddAction extends BaseAction {
  type: 'add';
  name: string;
  quantity: number;
  unit: string;
}

interface UpdateAction extends BaseAction {
  type: 'update';
  itemId: string;
  quantity: number;
}

type Action = RemoveAction | AddAction | UpdateAction;

export function validateAction(action: any, storeUnits: StoreUnit[]): Action | null {
  if (!action || typeof action !== 'object') {
    logger.debug('Invalid action: not an object', { component: 'MunshiService', data: { action } });
    return null;
  }

  logger.debug('Validating action', { component: 'MunshiService', data: { type: action.type, action } });

  switch (action.type) {
    case 'remove':
      if (!action.itemId || typeof action.itemId !== 'string') {
        logger.debug('Invalid remove action: missing or invalid itemId', { component: 'MunshiService', data: { action } });
        return null;
      }
      logger.debug('Valid remove action', { component: 'MunshiService', data: { itemId: action.itemId } });
      return { type: 'remove', itemId: action.itemId };

    case 'add':
      if (!action.name || typeof action.name !== 'string') {
        logger.debug('Invalid add action: missing or invalid name', { component: 'MunshiService', data: { action } });
        return null;
      }
      if (!action.quantity || typeof action.quantity !== 'number' || action.quantity <= 0) {
        logger.debug('Invalid add action: missing or invalid quantity', { component: 'MunshiService', data: { action } });
        return null;
      }
      // Check if unit is valid for this store
      const validUnit = storeUnits.find(u => u.name === action.unit);
      if (!validUnit) {
        logger.debug('Invalid add action: unit not found in store configuration', { component: 'MunshiService', data: { action, availableUnits: storeUnits.map(u => u.name) } });
        return null;
      }
      logger.debug('Valid add action', { component: 'MunshiService', data: { name: action.name, quantity: action.quantity, unit: action.unit } });
      return { 
        type: 'add', 
        name: action.name, 
        quantity: action.quantity, 
        unit: action.unit 
      };

    case 'update':
      if (!action.itemId || typeof action.itemId !== 'string') {
        logger.debug('Invalid update action: missing or invalid itemId', { component: 'MunshiService', data: { action } });
        return null;
      }
      if (!action.quantity || typeof action.quantity !== 'number' || action.quantity <= 0) {
        logger.debug('Invalid update action: missing or invalid quantity', { component: 'MunshiService', data: { action } });
        return null;
      }
      logger.debug('Valid update action', { component: 'MunshiService', data: { itemId: action.itemId, quantity: action.quantity } });
      return { 
        type: 'update', 
        itemId: action.itemId, 
        quantity: action.quantity 
      };

    default:
      logger.debug('Invalid action type', { component: 'MunshiService', data: { type: action.type } });
      return null;
  }
}

export const processUserInput = async (
  message: Message,
  messages: Message[],
  storeId: string,
  currentOrder: {
    customerName: string;
    customerPhone: string;
    lineItems: LineItem[];
  },
  storeUnits: StoreUnit[],
  appConfig: AppConfig,
  devInfoCb?: (info: { transcript?: string; llmActions?: any; validatedActions?: any; promptTokens?: number; completionTokens?: number }) => void
): Promise<ProcessResult> => {
  // Prepare agent context
  const context: AgentContext = {
    storeId,
    storeUnits,
    currentOrder,
    // Add more as needed
  };
  // Create the agent
  const agent = new InvoiceAgent(
    'InvoiceAgent',
    'Handles invoice-related user input and LLM processing for Munshi Ji.',
    context
  );
  // Call the agent's run method with the message and other needed info
  return agent.run({
    message,
    messages,
    appConfig,
    devInfoCb
  });
};

export const generateFinalResponse = async (promptContext: string[], appConfig: AppConfig): Promise<string> => {
  try {
    const { data, error } = await supabase.functions.invoke('call-llm', {
      body: { 
        userPrompt: promptContext.join('\n'), 
        model: appConfig.munshiJi.munshiModel, 
        temperature: 0.5 
      }
    });

    if (error) {
      logger.error('Error generating final response', { error, component: 'MunshiService' });
      return promptContext.join('\n');
    }

    return data.reply || promptContext.join('\n');
  } catch (error) {
    logger.error('Error in generateFinalResponse', { error, component: 'MunshiService' });
    return promptContext.join('\n');
  }
};

/**
 * Upsert (insert or update) a ChatSessionContext in Supabase
 */
export async function upsertChatSessionContext(context: ChatSessionContext): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase
      .from('chat_session_contexts')
      .upsert([
        {
          session_id: context.sessionId,
          user_id: context.userId,
          store_id: context.storeId,
          context: context,
        },
      ], { onConflict: 'session_id' });
    if (error) {
      logger.error('Failed to upsert chat session context', { error, component: 'MunshiService' });
      return { success: false, error: error.message };
    }
    return { success: true };
  } catch (err: any) {
    logger.error('Unexpected error upserting chat session context', { error: err, component: 'MunshiService' });
    return { success: false, error: err.message };
  }
} 